import CryptoJS from "crypto-js";
import ym from "react-yandex-metrika";
import { normalizeOfferId } from "./duplicateOffers";

export const isEmpty = (obj) => {
    for (let key in obj) {
        // если тело цикла начнет выполняться - значит в объекте есть свойства
        return false;
    }
    return true;
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const preloadedImageBlobs = new Map();
const preloadImagePromises = new Map();

export const preloadImages = async (images = {}) => {
    const preloadedImages = await Promise.all(
        Object.entries(images)
            .filter(([, src]) => Boolean(src))
            .map(async ([key, src]) => {
                if (preloadedImageBlobs.has(src)) {
                    return [key, preloadedImageBlobs.get(src)];
                }

                if (preloadImagePromises.has(src)) {
                    return [key, await preloadImagePromises.get(src)];
                }

                const preloadPromise = fetch(src)
                    .then(async (response) => {
                        if (!response.ok) throw new Error(`Failed to preload image: ${src}`);

                        const blob = await response.blob();
                        const image = { src, objectUrl: URL.createObjectURL(blob) };
                        preloadedImageBlobs.set(src, image);

                        return image;
                    })
                    .catch((error) => {
                        preloadImagePromises.delete(src);
                        throw error;
                    });
                preloadImagePromises.set(src, preloadPromise);

                const image = await preloadPromise;

                return [key, image];
            }),
    );

    return Object.fromEntries(preloadedImages);
};

const buildNestedObject = (keysAndValues, index = 0) => {
    if (index === keysAndValues.length - 1) {
        const { key } = keysAndValues[index];
        return key ? `${key}` : ""; // Последний уровень — пустая строка
    }

    const { key } = keysAndValues[index];
    return { [key]: buildNestedObject(keysAndValues, index + 1) };
};

const OFFER_ID_KEYS = new Set(["_id", "offerId"]);
const METRIC_OFFER_ID_KEYS = new Set(["_id", "offerId", "params2"]);

const normalizeIdsByKeys = (value, keys, key = "") => {
    if (typeof value === "string") return keys.has(key) ? normalizeOfferId(value) : value;
    if (Array.isArray(value)) return value.map((item) => normalizeIdsByKeys(item, keys, key));
    if (!value || typeof value !== "object" || Object.getPrototypeOf(value) !== Object.prototype) return value;

    return Object.fromEntries(Object.entries(value).map(([itemKey, item]) => [itemKey, normalizeIdsByKeys(item, keys, itemKey)]));
};

export const normalizeOfferIds = (value, key = "") => normalizeIdsByKeys(value, OFFER_ID_KEYS, key);

export const normalizeMetricOfferIds = (value, key = "") => normalizeIdsByKeys(value, METRIC_OFFER_ID_KEYS, key);

export const sendMetric = (goalName, metricData) => {
    const normalizedMetricData = normalizeMetricOfferIds(metricData);
    const keysAndValues = [
        { key: normalizedMetricData.params1 || "ris_promo", value: normalizedMetricData.params1 },
        { key: goalName, value: goalName },
        { key: normalizedMetricData.params2, value: normalizedMetricData.params2 },
        { key: normalizedMetricData.params3, value: normalizedMetricData.params3 },
        { key: normalizedMetricData.params4, value: normalizedMetricData.params4 },
        { key: normalizedMetricData.params5, value: normalizedMetricData.params5 },
        { key: normalizedMetricData.params6, value: normalizedMetricData.params6 },
        { key: normalizedMetricData.params7, value: normalizedMetricData.params7 },
        { key: normalizedMetricData.params8, value: normalizedMetricData.params8 },
        { key: normalizedMetricData.params9, value: normalizedMetricData.params9 },
    ].filter(({ key }) => key !== undefined);

    const metric = { reachGoal: buildNestedObject(keysAndValues) };

    ym("reachGoal", goalName, metric);
};

export const getWhatId = () => {
    const location_search = window?.location?.search || "";
    const urlParams = new URLSearchParams(location_search);
    let what_id = urlParams.get("what_id") || null;

    if (!what_id && location_search) {
        let firstIndex = location_search.indexOf("what_id=");
        if (firstIndex >= 0) {
            firstIndex = firstIndex + 8;
            const lastIndex = firstIndex + 24;
            what_id = location_search.substring(firstIndex, lastIndex);
        }
    }

    return what_id;
};

export const getExternalUuid = () => {
    const urlParams = new URLSearchParams(window?.location?.search || "");

    return urlParams.get("external_uuid") || urlParams.get("magnit_id") || null;
};

export const isTelegramBrowser = () => {
    if (typeof window === "undefined") return false;

    const userAgent = window.navigator?.userAgent || "";
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
    const hasTelegramLaunchParams = [url.searchParams, hashParams].some((params) => ["tgWebAppData", "tgWebAppPlatform", "tgWebAppVersion"].some((key) => params.has(key)));
    const hasTelegramBridge = Boolean(window.TelegramWebviewProxy || window.TelegramWebviewProxyProto || (window.Telegram?.WebApp?.platform && window.Telegram.WebApp.platform !== "unknown"));
    const hasTelegramUserAgent = /Telegram(?:-Android|Webview|\/)/i.test(userAgent);

    if (hasTelegramLaunchParams || hasTelegramBridge || hasTelegramUserAgent) return true;

    return false;
};

export const pluralForm = (n, arrType = []) => {
    return arrType[n % 10 == 1 && n % 100 != 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2];
};

export function getSqlServerGuidHash(offerCode, vendorId) {
    const code = offerCode ?? "!@#D";
    const vendor = vendorId ?? "!@#T";
    const combined = code + vendor;

    // Получаем MD5-хеш в виде слова (WordArray)
    const hash = CryptoJS.MD5(CryptoJS.enc.Utf8.parse(combined));

    // Преобразуем в массив байтов
    const hex = hash.toString(CryptoJS.enc.Hex);
    const bytes = [];

    for (let i = 0; i < hex.length; i += 2) {
        bytes.push(parseInt(hex.substr(i, 2), 16));
    }

    // SQL Server GUID порядок байт:
    // Byte order: 4-2-2-2-6 (но первые три группы в little-endian)
    const toLittleEndian = (arr) => arr.reverse();

    const part1 = toLittleEndian(bytes.slice(0, 4));
    const part2 = toLittleEndian(bytes.slice(4, 6));
    const part3 = toLittleEndian(bytes.slice(6, 8));
    const part4 = bytes.slice(8, 10);
    const part5 = bytes.slice(10, 16);

    const guidBytes = [...part1, ...part2, ...part3, ...part4, ...part5];

    // Форматируем как GUID
    const byteToHex = (b) => b.toString(16).padStart(2, "0");
    const guidHex = guidBytes.map(byteToHex);

    const guid = `${guidHex.slice(0, 4).join("")}-${guidHex.slice(4, 6).join("")}-${guidHex.slice(6, 8).join("")}-${guidHex.slice(8, 10).join("")}-${guidHex.slice(10, 16).join("")}`;

    return guid.toLowerCase();
}

export const updateParamWithoutRerender = (key, value) => {
    const url = new URL(window.location.href);

    if (value.trim() === "") {
        url.searchParams.delete(key);
    } else {
        url.searchParams.set(key, value);
    }
    window.history.pushState({}, "", url.toString());
};

export const clearParamWithoutRerender = (arr) => {
    const url = new URL(window.location.href);
    arr.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState({}, "", url.toString());
};

/** Нормализует ввод/вставку к 11 цифрам с ведущей 7 (8→7, убирает дубль 7/8 после +7 в маске) */
export const normalizePhoneDigits = (value) => {
    let digits = String(value ?? "").replace(/\D/g, "");
    if (!digits) return "";

    if (digits.startsWith("8") && digits.length === 11) {
        digits = `7${digits.slice(1)}`;
    } else if (digits.startsWith("78")) {
        digits = `7${digits.slice(2)}`;
    } else if (digits.startsWith("77") && digits.length > 11) {
        digits = `7${digits.slice(2)}`;
    } else if (digits.length === 10 && digits[0] !== "7") {
        digits = `7${digits}`;
    }

    if (digits.length > 11) {
        digits = digits.slice(0, 11);
    }

    return digits;
};

export function formatPhoneNumber(phone, isModal = false) {
    // Удаляем все нецифровые символы

    if (phone) {
        const cleanPhone = normalizePhoneDigits(phone);

        if (!cleanPhone.startsWith("7") && !cleanPhone.startsWith("8")) {
            return phone; // Возвращаем оригинальный номер если он не подходит
        }

        if (isModal) {
            return cleanPhone.replace(/^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/, "+$1 $2 $3-$4-$5");
        }

        return cleanPhone.replace(/^(\d)(\d{3})(\d{3})(\d{2})(\d{2})$/, "+$1 ($2) $3-$4-$5");
    }

    return "";
}

export const isStartScreenWidth = (width) => (window.innerWidth <= width ? true : false);

export const randomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

export const getNearValues = (maxValue, range = 16) => {
    if (!maxValue || maxValue <= 0) return [];
    const start = Math.max(1, maxValue - range);
    return Array.from({ length: maxValue - start + 1 }, (_, index) => start + index);
};

export const getNear = (min, max, base, delta = 3) => {
    const value = base + randomNumber(-delta, delta);
    return Math.max(min, Math.min(max, value));
};

export const getAbTestMetrika = (abTest = {}, id = "") => {
    if (abTest && id) {
        for (let [key, value] of Object.entries(abTest)) {
            if (key.split("_").includes(normalizeOfferId(id))) {
                return value;
            }
        }
    }

    return false;
};

export const getCookie = (name) => {
    const cookiesArray = document.cookie.split("; ");
    for (let i = 0; i < cookiesArray.length; i++) {
        const [cookieName, cookieValue] = cookiesArray[i].split("=");
        if (cookieName === name) {
            return cookieValue;
        }
    }
    return null;
};

const BLOCKED_DOMAINS = new Set([
    "mail.com",
    "gmail.ru",
    "gmail.co",
    "qmail.com",
    "gmal.com",
    "proton.me",
    "com.ru",
    "mai.ru",
    "yndex.ru",
    "gimail.com",
    "gmaill.com",
    "jandex.ru",
    "cmail.com",
    "gmali.com",
    "mal.ru",
    "gamil.com",
    "gmaii.com",
    "ayndex.ru",
    "email.ru",
    "mali.ru",
    "dmail.com",
    "msil.ru",
    "gemail.com",
    "yanbex.ru",
    "yandeks.ru",
    "gamail.com",
    "iclaud.com",
    "yangex.ru",
    "gnail.com",
    "qmail.ru",
    "imail.ru",
    "hmail.com",
    "cloud.com",
    "gail.com",
    "gmail.com.ru",
    "maul.ru",
    "meil.ru",
    "mall.ru",
    "lust.ru",
    "mail.ru.ru",
    "bl.ru",
    "icloud.ru",
    "mmail.ru",
    "yandx.ru",
    "yanfex.ru",
    "gmaul.com",
    "yadex.ru",
    "yandez.ru",
    "yande.ru",
    "gmall.com",
    "icloid.com",
    "gimal.com",
    "iclou.com",
    "icloud.co",
    "maizl.ru",
    "mail.eu",
    "gmael.com",
    "ysndex.ru",
    "uandex.ru",
    "mael.ru",
    "icoud.com",
    "yandes.ru",
    "iclod.com",
    "yandex.ru.ru",
    "yandec.ru",
    "iclud.com",
    "invox.ru",
    "yanex.ru",
    "dk.ru",
    "gmail.cm",
    "yandex.eu",
    "inbox.com",
    "bj.ru",
    "gmaik.com",
    "lcloud.com",
    "lisr.ru",
    "handed.ru",
    "ramblet.ru",
    "andex.ru",
    "indox.ru",
    "igmail.com",
    "maiil.ru",
    "lnbox.ru",
    "main.ru",
    "mail.co",
    "gmial.com",
    "icliud.com",
    "tandex.ru",
    "gmmail.com",
    "3gmail.com",
    "maik.ru",
    "yundex.ru",
    "ail.com",
    "vmail.com",
    "mai.com",
    "gmayl.com",
    "ynbex.ru",
    "ucloud.com",
    "icloyd.com",
    "yandrx.ru",
    "maii.ru",
]);

export const validateEmail = (email) => {
    if (typeof email !== "string") return false;
    const normalizedEmail = email.toLowerCase().trim();

    // Базовая структура: что-то@что-то.что-то
    if (!normalizedEmail.includes("@")) return false;
    const [local, domainFull] = normalizedEmail.split("@");
    if (!local || !domainFull) return false;

    // Проверка домена (оставим вашу логику, но уберём ограничение на длину TLD)
    const domainPattern = /^([\w-]{2,}\.)+[\w-]{2,}$/;
    if (!domainPattern.test(domainFull)) return false;

    if (BLOCKED_DOMAINS.has(domainFull)) return false;

    // --- Проверка локальной части ---
    const localPattern = /^[a-z0-9][a-z0-9+_.-]*[a-z0-9_]$/;
    if (!localPattern.test(local)) return false;

    // Запрет двух точек подряд (или точки рядом с +, _, - — не требуется по ТЗ, но .. точно запрещено)
    if (local.includes("..")) return false;

    // --- Дополнительные проверки (ваши правила) ---
    const domainParts = domainFull.split(".");
    const mainDomain = domainParts[0];

    if (local.length < 2 || mainDomain.length < 2) return false;
    if (local === mainDomain) return false;

    const stopWords = ["test", "asdf", "qwert", "email", "123", "abc", "none", "fake", "null"];
    if (stopWords.some((word) => local.startsWith(word))) return false;

    // Убираем не-буквенно-цифровые символы для анализа повторов (оставляем только a-z0-9)
    const cleanLocal = local.replace(/[^a-z0-9]/g, "");
    const cleanDomain = mainDomain.replace(/[^a-z0-9]/g, "");

    if (cleanLocal.length === 0 || cleanDomain.length === 0) return false;

    // Полностью одинаковые символы (только среди букв/цифр)
    const sameCharOnlyPattern = /^([a-z0-9])\1+$/;
    if (sameCharOnlyPattern.test(cleanLocal) || sameCharOnlyPattern.test(cleanDomain)) return false;

    // Доля самого частого символа > 70%
    const isTooRepetitive = (str) => {
        const freq = {};
        for (const c of str) {
            freq[c] = (freq[c] || 0) + 1;
        }
        const maxCount = Math.max(...Object.values(freq));
        return maxCount / str.length > 0.7;
    };

    if (isTooRepetitive(cleanLocal) || isTooRepetitive(cleanDomain)) return false;

    return true;
};

export const validatePhone = (phone, maskType = "+7 (___) ___-__-__") => {
    const maskValidation = maskType.replace(/[\s\(\)\+-]/g, "");
    const phoneVaildation = phone.replace(/[\s\(\)\+-]/g, "");
    const digitsOnly = phone.replace(/\D/g, "");

    if (digitsOnly.length !== maskValidation.length) return false;

    const repeatedCharPattern = /(\d)\1{6,}/;
    if (repeatedCharPattern.test(phoneVaildation)) return false;

    const isPhoneRF = /^(?:\+7|7|8)(?:[9])\d{9}$/;
    if (!isPhoneRF.test(phoneVaildation)) return false;

    return true;
};

export const parseAbQuery = () => {
    const QUERY_TO_AB_KEY = {
        ab_offers: "abOffers",
        ab_sites: "abSites",
        widgets: "lego",
    };
    // ?ab_offers=valera_ivi_v1,igor_yandex_v3&ab_sites=site_a_v1,site_b_v2&widgets=ya_v1,b_sample_v2
    const urlParams = new URLSearchParams(window.location.search);
    const result = {
        abOffers: [],
        abSites: [],
        lego: [],
    };

    Object.entries(QUERY_TO_AB_KEY).forEach(([queryKey, abKey]) => {
        const paramValue = urlParams.get(queryKey);

        if (paramValue) {
            result[abKey] = paramValue
                .split(",")
                .map((item) => item.trim())
                .filter(Boolean);
        }
    });

    return { abObject: result };
};

export function checkReachGoalReady(maxAttempts = 10, interval = 500) {
    return new Promise((resolve, reject) => {
        let attempts = 0;

        const checkInterval = setInterval(() => {
            attempts++;

            if (window.risMetrika && typeof window.risMetrika.reachgoal_ch === "function") {
                clearInterval(checkInterval);
                resolve(true);
            } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                reject(new Error("reachgoal_ch не загружена"));
            }
        }, interval);
    });
}

export const fixOrphans = (text) => {
    const NBSP = "&nbsp;";
    const NBHY = "&#8209;";
    const WJ = "&#8288;";
    const MAX_NBSP_CHAIN = 3;

    let s = String(text ?? "");
    s = s.replace(/&nbsp;|\u00A0/g, " ");

    s = s.replace(/([\p{L}\p{N}])-(?=[\p{L}\p{N}])/gu, `$1${NBHY}`);
    s = s.replace(/([\p{L}\p{N}])\/(?=[\p{L}\p{N}])/gu, `$1${WJ}/${WJ}`);
    s = s.replace(/\+\s+(?=[\p{L}\p{N}])/gu, `+${NBSP}`);

    s = s.replace(/(^|[^\p{L}\p{N}])((?:[\p{L}\p{N}]{1,3}\s+)+)(?=[\p{L}\p{N}])/gu, (_match, boundary, shortWords) => `${boundary}${shortWords.replace(/\s+/g, NBSP)}`);
    s = s.replace(new RegExp(`(\\d{2,3}(?:${NBSP}\\d{3})+)${NBSP}(?=[\\p{L}])`, "gu"), "$1 ");
    s = s.replace(new RegExp(`((?:${NBSP}[\\p{L}\\p{N}]+){${MAX_NBSP_CHAIN}})${NBSP}`, "gu"), "$1 ");

    return s;
};

export const saveToLocalStorage = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
};

export const getStoredLocation = () => {
    const value = localStorage.getItem("locale_ris_promo");

    if (!value || value === "undefined" || value === "null") return null;

    try {
        const location = JSON.parse(value);
        return location?._id && location?.city ? location : null;
    } catch {
        return null;
    }
};

export const isCaptchaEnabled = false;
// export const isCaptchaEnabled = !((VITE_NODE_ENV || "").toLowerCase() === "development");

export const modifyData = (arr, key) => Object.fromEntries(arr.map((row) => [row[key], row]));

export const ensureVariantCookie = async (maxWaitMs = 2000) => {
    const read = () => {
        const cookie = document.cookie.match(/(?:^|; )_r_p_s_variant=([^|;]+)/)?.[1] ?? null;
        console.log("ensureVariantCookie", cookie);
        return cookie;
    };
    if (read()) return read();

    await fetch(`${window.location.origin}/`, {
        credentials: "include",
        cache: "no-store",
    });

    const deadline = performance.now() + maxWaitMs;
    while (performance.now() < deadline) {
        const v = read();
        if (v) return v;
        await new Promise((r) => setTimeout(r, 50));
    }
    return null;
};

export const getOfferLegalInfo = (offer, filialObj) => {
    const addressCities = offer?.addresses?.addresses ?? [];
    const hasFilialLegalData = addressCities.some((city) => city.address?.some((address) => address.ord || address.vendorId || address.vendorName));
    const selectedFilial = addressCities.find((city) => city.city === filialObj?.city)?.address?.find((address) => address.text === filialObj?.filial);

    return {
        showLegalInfo: !hasFilialLegalData || Boolean(selectedFilial),
        vendorId: selectedFilial?.vendorId || offer?.vendor?._id,
        vendorName: selectedFilial?.vendorName || offer?.vendor?.vendorInfo?.fullOpf,
        ord: selectedFilial?.ord || offer?.offerDetail?.ordRisPromo,
    };
};
